import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { deleteDashboardById, getCurrentUser, getDashboardById, getDashboardEmbedTokens } from '../../../domain/domain'
import { useOrganizationProvider } from '../../../providers/OrganizationProvider'
import { IDashboardTokens } from '../../../domain/interfaces/IDashboard'
import {
  hideConfirmationDialog,
  showConfirmationDialog,
} from '../../components/dialogs/confirmation-dialog/ConfirmationDialog'
import { ErrorToast, SuccessToast } from '../../../core/utils/toast-notifications/ToastNotifications'

interface IDashboardFilteringContextType {
  tokens: IDashboardTokens | null
  loading: boolean
  title: string
  getTokens: () => void
  errorObj: any
  error: boolean
  deleteDashboard: (id: string) => void
}

// Create the context
const DashboardFilteringPageViewModelContext = React.createContext<IDashboardFilteringContextType>(
  {} as IDashboardFilteringContextType,
)
DashboardFilteringPageViewModelContext.displayName = 'DashboardFilteringPageViewModelContext'

// Helper function to create filter values (similar to getFilterValues in PHP)
// from domo php example
const createFilterValues = (columnName: string, values: string[]) => {
  return {
    columnName: columnName,
    values: values,
    operator: 'IN',
  }
}

export const DashboardFilteringPageViewModel: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [tokens, setTokens] = useState<IDashboardTokens | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [errorObj, setErrorObj] = useState<any>(null)
  const [title, setTitle] = useState<string>('Dashboard')
  const navigate = useNavigate()
  const { id } = useParams()
  const user = getCurrentUser()
  const orgProvider = useOrganizationProvider()

  const deleteDashboard = async (id: string) => {
    showConfirmationDialog({
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this dashboard from the database? This action cannot be undone.',
      cancelText: 'Cancel',
      confirmText: 'Delete',
      onCancel: function (): void {
        hideConfirmationDialog()
      },
      onConfirm: async () => {
        const results = await deleteDashboardById(id)
        if (results.isLeft()) {
          ErrorToast({
            title: 'Error removing dashboard.',
          })
          return
        }

        SuccessToast({
          title: 'Dashboard has been deleted successfully.',
        })

        navigate(-1)
        hideConfirmationDialog()
      },
    })
  }

  const getTokens = useCallback(async () => {
    setLoading(true)
    setError(false)
    setErrorObj(null)

    try {
      // Apply filter logic according to requirements
      // 1: if project fence is enabled AND user is assigned any project
      const projectFenceEnabled = orgProvider.org?.isProjectFenceEnabled
      const levelFenceEnabled = orgProvider.org?.isLevelFenceEnabled
      const isDashboardFencesEnabled = orgProvider.org?.isDashboardFencesEnabled
      const userProjects = user?.projects ?? []
      const userLevels = user?.levels ?? []

      if (!id) return
      const res = await getDashboardById(id)

      if (res.isLeft()) {
        setErrorObj(res.left)
        // setError(res.left?.message ?? 'Error loading dashboard.')
        setError(true)
        return
      }
      let embedId = ''
      // let embedToken = ''
      if (res.right) {
        embedId = res.right.embedId
      }

      // Default to empty array if filters are empty it returns everything
      const filters = []

      // only apply if org has flag set
      if (isDashboardFencesEnabled) {
        // 1. Project fence logic
        if (projectFenceEnabled && userProjects.length > 0) {
          filters.push({
            column: 'projectuserid',
            values: [user?.id.toString() ?? ''],
            operator: 'EQUALS',
          })
        }

        // 2. Level fence logic
        if (levelFenceEnabled && userLevels.length > 0) {
          filters.push({
            column: 'divisionuserid',
            values: [user?.id.toString() ?? ''],
            operator: 'EQUALS',
          })
        }
      }

      const embedResponse = await getDashboardEmbedTokens({
        embedId: embedId,
        permissions: ['READ', 'FILTER', 'EXPORT'],
        filters: filters, // if filters are empty it returns everything
      })

      if (embedResponse.right) {
        // Set the tokens
        setTokens({
          embedId: embedResponse.right.embedId,
          embedToken: embedResponse.right.embedToken,
        })
        setLoading(false)
        return
      } else {
        setError(true)
      }
    } catch (err: any) {
      console.error('Error fetching Domo tokens:', err)
      setError(true)
      setErrorObj(err)
    } finally {
      setLoading(false)
    }
  }, [orgProvider, user])

  useEffect(() => {
    if (!orgProvider.isDemoAccount) {
      getTokens()
    } else {
      setLoading(false)
    }
  }, [orgProvider.isDemoAccount])

  const value: IDashboardFilteringContextType = {
    tokens,
    loading,
    title,
    getTokens,
    errorObj,
    error,
    deleteDashboard,
  }

  return (
    <DashboardFilteringPageViewModelContext.Provider value={value}>
      {children}
    </DashboardFilteringPageViewModelContext.Provider>
  )
}

export const useDashboardFilteringPageViewModel = () => {
  const context = useContext(DashboardFilteringPageViewModelContext)
  if (!context) {
    throw new Error('useDashboardFilteringPageViewModel must be used within a DashboardFilteringPageViewModel')
  }
  return context
}
